import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

/**
 * MetricsCard, v2
 * Cleaner visual hierarchy, subtle gradient accent on alert state,
 * better number formatting, optional footnote citing directive article.
 */
const MetricsCard = ({
  title,
  value,
  unit = '%',
  change,
  changeType,
  icon,
  alert,
  description,
  directiveRef,
}) => {
  const { t } = useTranslation();
  const changeColor =
    changeType === 'positive' ? 'text-success'
    : changeType === 'negative' ? 'text-destructive'
    : 'text-muted-foreground';

  const changeIcon =
    changeType === 'positive' ? 'TrendingDown'
    : changeType === 'negative' ? 'TrendingUp'
    : 'Minus';

  const alertStyles =
    alert === 'critical' ? 'border-destructive/30 bg-gradient-to-br from-destructive/5 to-transparent'
    : alert === 'warning' ? 'border-warning/30 bg-gradient-to-br from-warning/5 to-transparent'
    : 'border-border bg-card';

  const iconStyles =
    alert === 'critical' ? 'bg-destructive/10 text-destructive'
    : alert === 'warning' ? 'bg-warning/10 text-warning'
    : 'bg-primary/10 text-primary-solid';

  return (
    <div className={`relative p-5 rounded-lg border transition-smooth hover:shadow-soft ${alertStyles}`}>
      {alert && (
        <div className="absolute top-3 right-3">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
            alert === 'critical' ? 'bg-destructive text-destructive-foreground'
            : 'bg-warning text-warning-foreground'
          }`}>
            {alert === 'critical' && <Icon name="AlertTriangle" size={10} />}
            {alert === 'critical' ? t('dashboard.metrics.breach') : t('dashboard.metrics.watch')}
          </span>
        </div>
      )}

      <div className="flex items-center gap-2.5 mb-3">
        <div className={`p-1.5 rounded-md ${iconStyles}`}>
          <Icon name={icon} size={16} />
        </div>
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {title}
        </h3>
      </div>

      <div className="flex items-baseline gap-1 mb-2">
        <span className="text-3xl font-bold text-foreground tabular-nums leading-none">
          {value}
        </span>
        {unit && (
          <span className="text-lg font-semibold text-muted-foreground leading-none">{unit}</span>
        )}
      </div>

      {change !== undefined && change !== null && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <Icon name={changeIcon} size={14} className={changeColor} />
          <span className={`text-xs font-semibold tabular-nums ${changeColor}`}>
            {change > 0 ? '+' : ''}{change}{unit}
          </span>
          <span className="text-xs text-muted-foreground">{t('dashboard.metrics.vsPriorYear')}</span>
        </div>
      )}

      {description && (
        <p className="text-xs text-muted-foreground leading-snug">{description}</p>
      )}

      {directiveRef && (
        <div className="mt-3 pt-3 border-t border-border/60">
          <span className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground uppercase tracking-wide">
            <Icon name="BookOpen" size={10} />
            {t('dashboard.metrics.directive', { ref: directiveRef })}
          </span>
        </div>
      )}
    </div>
  );
};

export default MetricsCard;
